package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.style.*
import se.mittpass.shared.api.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

private val sbScope = MainScope()

private fun calculatePatternBreak(startTime: String, endTime: String): Int {
    val sParts = startTime.split(":").mapNotNull { it.toIntOrNull() }
    val eParts = endTime.split(":").mapNotNull { it.toIntOrNull() }
    if (sParts.size < 2 || eParts.size < 2) return 0
    val durationMin = (eParts[0] * 60 + eParts[1]) - (sParts[0] * 60 + sParts[1])
    return calculateDefaultBreakMinutes(durationMin)
}

data class ScheduleTemplateDto(
    val id: String,
    val name: String,
    val description: String,
    val isDeletable: Boolean = true,
    val isEditable: Boolean = true,
    val patterns: List<DayPatternItem>
)

private fun getDefaultTemplates(): List<ScheduleTemplateDto> {
    return listOf(
        ScheduleTemplateDto(
            id = "empty",
            name = "Tomt schema",
            description = "Börja från ett helt tomt schema utan förskonfigurerade pass.",
            isDeletable = false,
            isEditable = false,
            patterns = emptyList()
        ),
        ScheduleTemplateDto(
            id = "normal",
            name = "Normalvecka",
            description = "Mån–Tor 1 pass (08–17), Fre–Lör 2 pass (08–17 & 12–17), Sön 10–16.",
            isDeletable = true,
            isEditable = true,
            patterns = createDefaultNormalPatterns()
        ),
        ScheduleTemplateDto(
            id = "summer",
            name = "Sommarschema",
            description = "Högintensiv säsong. Mån–Sön 2 dubbelpass dagligen (08–17 & 12–20).",
            isDeletable = true,
            isEditable = true,
            patterns = createSummerPatterns()
        ),
        ScheduleTemplateDto(
            id = "weekend",
            name = "Helgtopp",
            description = "Mån–Tor basbemanning 08–17, Fre–Sön dubbelpass (08–17 & 12–21).",
            isDeletable = true,
            isEditable = true,
            patterns = createWeekendPeakPatterns()
        )
    )
}

private fun loadAllTemplates(): List<ScheduleTemplateDto> {
    return try {
        val jsonStr = kotlinx.browser.window.localStorage.getItem("mittpass_templates_v3")
        if (!jsonStr.isNullOrEmpty()) {
            val parsed: Array<dynamic> = kotlin.js.JSON.parse(jsonStr)
            parsed.map { item ->
                val patArray: Array<dynamic> = item.patterns ?: emptyArray<dynamic>()
                val patternsList = patArray.map { p ->
                    val sTime = p.startTime as String
                    val eTime = p.endTime as String
                    val bMin = (p.breakMinutes as? Number)?.toInt() ?: calculatePatternBreak(sTime, eTime)
                    DayPatternItem(
                        dayOfWeek = (p.dayOfWeek as Number).toInt(),
                        startTime = sTime,
                        endTime = eTime,
                        assignedToUserId = (p.assignedToUserId as? Number)?.toLong(),
                        breakMinutes = bMin
                    )
                }
                ScheduleTemplateDto(
                    id = item.id as String,
                    name = item.name as String,
                    description = (item.description as? String) ?: "",
                    isDeletable = (item.isDeletable as? Boolean) ?: true,
                    isEditable = (item.isEditable as? Boolean) ?: true,
                    patterns = patternsList
                )
            }
        } else {
            getDefaultTemplates()
        }
    } catch (e: Exception) {
        getDefaultTemplates()
    }
}

private fun saveAllTemplatesToStorage(templates: List<ScheduleTemplateDto>) {
    try {
        val rawArray = templates.map { tpl ->
            val patArray = tpl.patterns.map { p ->
                val pObj = js("{}")
                pObj["dayOfWeek"] = p.dayOfWeek
                pObj["startTime"] = p.startTime
                pObj["endTime"] = p.endTime
                pObj["breakMinutes"] = p.breakMinutes
                pObj["assignedToUserId"] = p.assignedToUserId
                pObj
            }.toTypedArray()

            val tObj = js("{}")
            tObj["id"] = tpl.id
            tObj["name"] = tpl.name
            tObj["description"] = tpl.description
            tObj["isDeletable"] = tpl.isDeletable
            tObj["isEditable"] = tpl.isEditable
            tObj["patterns"] = patArray
            tObj
        }.toTypedArray()

        val jsonStr = kotlin.js.JSON.stringify(rawArray)
        kotlinx.browser.window.localStorage.setItem("mittpass_templates_v3", jsonStr)
    } catch (e: Exception) {
        println("Error saving templates: $e")
    }
}

@Composable
fun ScheduleBuilderPage(
    workplaceId: Long,
    onNavigateToSchema: (() -> Unit)? = null
) {
    var step by remember { mutableStateOf(1) }
    var startDate by remember { mutableStateOf(mondayOf(todayLocalDate())) }
    var numberOfWeeks by remember { mutableStateOf(4) }
    var selectedTemplateId by remember { mutableStateOf("normal") }
    var editingTemplateId by remember { mutableStateOf<String?>(null) }
    var overwriteExisting by remember { mutableStateOf(false) }

    var staff by remember { mutableStateOf<List<StaffMemberDto>>(emptyList()) }
    var isSubmitting by remember { mutableStateOf(false) }
    var isGenerated by remember { mutableStateOf(false) }
    var generatedCount by remember { mutableStateOf<Int?>(null) }
    var errorMsg by remember { mutableStateOf<String?>(null) }

    // Templates state
    var templates by remember { mutableStateOf(loadAllTemplates()) }
    var templateNameInput by remember { mutableStateOf("") }
    var templateSavedFeedback by remember { mutableStateOf<String?>(null) }

    // Patterns state: List of DayPatternItem (dayOfWeek: 1..7)
    var patterns by remember {
        mutableStateOf(createDefaultNormalPatterns())
    }

    LaunchedEffect(workplaceId) {
        staffRepo.getStaff(workplaceId).onSuccess { staff = it }
    }

    fun applyTemplate(tpl: ScheduleTemplateDto) {
        selectedTemplateId = tpl.id
        editingTemplateId = if (tpl.isEditable) tpl.id else null
        templateNameInput = if (tpl.isEditable) tpl.name else ""
        patterns = tpl.patterns.map { it.copy() }
    }

    fun saveCurrentPatternAsTemplate(name: String) {
        if (name.isBlank()) return
        val existingIdx = templates.indexOfFirst { it.id == editingTemplateId }
        val targetId = if (existingIdx >= 0 && templates[existingIdx].isEditable) {
            templates[existingIdx].id
        } else {
            "custom_${kotlin.js.Date.now().toLong()}"
        }

        val newTpl = ScheduleTemplateDto(
            id = targetId,
            name = name.trim(),
            description = "${patterns.size} pass per vecka.",
            isDeletable = true,
            isEditable = true,
            patterns = patterns.map { it.copy() }
        )

        val updatedList = templates.toMutableList()
        if (existingIdx >= 0 && updatedList[existingIdx].isEditable) {
            updatedList[existingIdx] = newTpl
        } else {
            updatedList.add(newTpl)
        }

        templates = updatedList
        saveAllTemplatesToStorage(updatedList)
        selectedTemplateId = targetId
        editingTemplateId = targetId
        templateSavedFeedback = "Mallen \"${name.trim()}\" har sparats!"
    }

    fun deleteTemplate(id: String) {
        val target = templates.find { it.id == id }
        if (target != null && target.isDeletable) {
            val updated = templates.filter { it.id != id }
            templates = updated
            saveAllTemplatesToStorage(updated)
            if (selectedTemplateId == id) {
                val fallback = updated.firstOrNull { it.id == "normal" } ?: updated.firstOrNull()
                if (fallback != null) applyTemplate(fallback)
            }
        }
    }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1.25rem")
        }
    }) {
        // ── Header Banner (Clean Modern Design Language) ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "20px")
                property("padding", "1.35rem 1.6rem")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("gap", "1rem")
                property("flex-wrap", "wrap")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.03)")
            }
        }) {
            Div {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem"); property("margin-bottom", "0.25rem") } }) {
                    Span({
                        style {
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("padding", "0.2rem 0.65rem")
                            property("border-radius", "50px")
                            property("font-size", "0.72rem")
                            property("font-weight", "800")
                            property("letter-spacing", "0.03em")
                            property("border", "1px solid var(--rp-info-border)")
                        }
                    }) { Text("SCHEMA") }
                    H2({
                        style {
                            property("margin", "0")
                            property("font-size", "1.35rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Skapa nytt schema") }
                }
                P({
                    style {
                        property("margin", "0")
                        property("color", "var(--rp-subtle)")
                        property("font-size", "0.85rem")
                        property("font-weight", "500")
                    }
                }) { Text("Massgenerera och rulla ut veckovisa scheman för flera veckor i taget") }
            }

            // Stepper Pills Header
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.35rem")
                    property("background", "var(--rp-surface)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "0.3rem 0.5rem")
                    property("border-radius", "50px")
                }
            }) {
                StepBadge(1, "Period & Mall", step == 1, step > 1) { step = 1 }
                FaIcon("fa-solid fa-chevron-right", size = "0.65rem", color = "var(--rp-hl-med)")
                StepBadge(2, "Veckomall & Pass", step == 2, step > 2) { step = 2 }
                FaIcon("fa-solid fa-chevron-right", size = "0.65rem", color = "var(--rp-hl-med)")
                StepBadge(3, "Förhandsgranska", step == 3, isGenerated) { step = 3 }
            }
        }

        // Error Feedback
        errorMsg?.let { msg ->
            Div({
                style {
                    property("background", "var(--rp-err-bg)")
                    property("border", "1px solid var(--rp-err-border)")
                    property("border-radius", "14px")
                    property("padding", "0.85rem 1.1rem")
                    property("color", "var(--rp-err-text)")
                    property("font-weight", "700")
                    property("font-size", "0.88rem")
                }
            }) { Text(msg) }
        }

        // ── STEP 1: Tidsperiod & Mall ──
        if (step == 1) {
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "20px")
                    property("padding", "1.5rem 1.75rem")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "1.5rem")
                }
            }) {
                Div {
                    H3({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("font-size", "1.15rem"); property("color", "var(--rp-text)") } }) {
                        Text("Steg 1: Välj Tidsperiod & Schemamall")
                    }
                    P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                        Text("Ange startdatum för rullningen, antal veckor samt välj eller redigera en mall.")
                    }
                }

                // Period Inputs
                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                        property("gap", "1.25rem")
                    }
                }) {
                    Div {
                        Label(forId = "startDate", attrs = { style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("display", "block"); property("margin-bottom", "0.35rem") } }) {
                            Text("Startdatum (Måndag)")
                        }
                        Input(type = InputType.Date) {
                            id("startDate")
                            value(startDate)
                            onInput { e ->
                                val selected = e.value
                                if (selected.isNotEmpty()) {
                                    startDate = mondayOf(selected)
                                }
                            }
                            style {
                                property("width", "100%")
                                property("padding", "0.6rem 0.85rem")
                                property("border", "1.5px solid var(--rp-hl-med)")
                                property("border-radius", "10px")
                                property("font-size", "0.9rem")
                                property("font-family", "inherit")
                            }
                        }
                    }

                    Div {
                        Label(attrs = { style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("display", "block"); property("margin-bottom", "0.35rem") } }) {
                            Text("Antal veckor framåt")
                        }
                        Div({ style { property("display", "flex"); property("gap", "0.45rem"); property("flex-wrap", "wrap") } }) {
                            listOf(1, 2, 4, 8, 12).forEach { w ->
                                val isSel = numberOfWeeks == w
                                Button({
                                    onClick { numberOfWeeks = w }
                                    style {
                                        property("padding", "0.5rem 1rem")
                                        property("border", if (isSel) "none" else "1px solid var(--rp-hl-med)")
                                        property("background", if (isSel) "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))" else "var(--rp-surface)")
                                        property("color", if (isSel) "#FFFFFF" else "var(--rp-subtle)")
                                        property("border-radius", "50px")
                                        property("font-weight", "700")
                                        property("font-size", "0.82rem")
                                        property("cursor", "pointer")
                                        property("font-family", "inherit")
                                        property("box-shadow", if (isSel) "0 2px 8px rgba(108,60,225,0.25)" else "none")
                                        property("transition", "all 0.15s ease")
                                    }
                                }) { Text("$w ${if (w == 1) "vecka" else "veckor"}") }
                            }
                        }
                    }
                }

                // Templates Selection Grid
                Div {
                    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-bottom", "0.65rem") } }) {
                        Label(attrs = { style { property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                            Text("Tillgängliga schemamallar")
                        }
                        Span({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) {
                            Text("Välj en mall som grund för passkonfigurationen")
                        }
                    }
                    Div({
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(auto-fit, minmax(230px, 1fr))")
                            property("gap", "1rem")
                        }
                    }) {
                        templates.forEach { tpl ->
                            val isSel = selectedTemplateId == tpl.id
                            Div({
                                style {
                                    property("background", if (isSel) "var(--rp-info-bg)" else "var(--rp-surface)")
                                    property("border", if (isSel) "2px solid var(--rp-iris)" else "1px solid var(--rp-hl-med)")
                                    property("border-radius", "16px")
                                    property("padding", "1.2rem")
                                    property("cursor", "pointer")
                                    property("transition", "all 0.15s ease")
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("justify-content", "space-between")
                                    property("box-shadow", if (isSel) "0 6px 18px rgba(124,58,237,0.12)" else "0 1px 3px rgba(0,0,0,0.02)")
                                }
                                onClick { applyTemplate(tpl) }
                            }) {
                                Div {
                                    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-bottom", "0.45rem") } }) {
                                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem") } }) {
                                            val iconName = when (tpl.id) {
                                                "empty" -> "fa-file-lines"
                                                "summer" -> "fa-sun"
                                                "weekend" -> "fa-star"
                                                else -> "fa-calendar-week"
                                            }
                                            FaIcon("fa-solid $iconName", size = "0.85rem", color = if (isSel) "var(--rp-iris)" else "var(--rp-subtle)")
                                            H4({ style { property("margin", "0"); property("font-weight", "800"); property("font-size", "0.95rem"); property("color", if (isSel) "var(--rp-text)" else "var(--rp-text)") } }) {
                                                Text(tpl.name)
                                            }
                                        }
                                        if (tpl.isDeletable) {
                                            Button({
                                                onClick { it.stopPropagation(); deleteTemplate(tpl.id) }
                                                style {
                                                    property("border", "none")
                                                    property("background", "var(--rp-err-bg)")
                                                    property("color", "var(--rp-err-text)")
                                                    property("border-radius", "50%")
                                                    property("width", "22px")
                                                    property("height", "22px")
                                                    property("font-size", "0.7rem")
                                                    property("font-weight", "800")
                                                    property("cursor", "pointer")
                                                    property("display", "flex")
                                                    property("align-items", "center")
                                                    property("justify-content", "center")
                                                    property("transition", "background 0.15s ease")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-xmark", size = "0.7rem", color = "var(--rp-err-text)")
                                            }
                                        }
                                    }
                                    P({ style { property("margin", "0 0 0.85rem 0"); property("font-size", "0.78rem"); property("color", "var(--rp-subtle)"); property("line-height", "1.4") } }) {
                                        Text(tpl.description)
                                    }
                                }

                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center") } }) {
                                    Span({
                                        style {
                                            property("font-size", "0.74rem")
                                            property("font-weight", "700")
                                            property("color", if (isSel) "var(--rp-iris)" else "var(--rp-subtle)")
                                            property("background", if (isSel) "var(--rp-info-bg)" else "var(--rp-overlay)")
                                            property("padding", "0.2rem 0.55rem")
                                            property("border-radius", "50px")
                                        }
                                    }) {
                                        Text(if (tpl.patterns.isEmpty()) "Tomt schema" else "${tpl.patterns.size} pass/vecka")
                                    }
                                    if (tpl.isEditable) {
                                        Button({
                                            onClick {
                                                it.stopPropagation()
                                                applyTemplate(tpl)
                                                step = 2
                                            }
                                            style {
                                                property("padding", "0.25rem 0.65rem")
                                                property("border", "1px solid var(--rp-info-border)")
                                                property("background", "var(--rp-surface)")
                                                property("color", "var(--rp-iris)")
                                                property("border-radius", "50px")
                                                property("font-size", "0.74rem")
                                                property("font-weight", "700")
                                                property("cursor", "pointer")
                                                property("font-family", "inherit")
                                            }
                                        }) { Text("Redigera") }
                                    }
                                }
                            }
                        }
                    }
                }

                // Action Bar
                Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("margin-top", "0.75rem") } }) {
                    Button({
                        onClick { step = 2 }
                        style {
                            property("padding", "0.65rem 1.6rem")
                            property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "#FFFFFF")
                            property("border-radius", "50px")
                            property("font-weight", "700")
                            property("font-size", "0.88rem")
                            property("cursor", "pointer")
                            property("font-family", "inherit")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                            property("box-shadow", "0 2px 10px rgba(108,60,225,0.25)")
                            property("transition", "all 0.15s ease")
                        }
                    }) {
                        Text("Nästa: Konfigurera veckomall")
                        FaIcon("fa-solid fa-arrow-right", size = "0.8rem", color = "var(--rp-surface)")
                    }
                }
            }
        }

        // ── STEP 2: Veckomall & Pass per Dag (Mån–Sön) ──
        if (step == 2) {
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "20px")
                    property("padding", "1.5rem 1.75rem")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "1.25rem")
                }
            }) {
                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("flex-wrap", "wrap"); property("gap", "0.75rem") } }) {
                    Div {
                        H3({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("font-size", "1.15rem"); property("color", "var(--rp-text)") } }) {
                            Text("Steg 2: Konfigurera Pass per Veckodag")
                        }
                        P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                            Text("Lägg till pass per dag, sätt start/sluttid och välj förvald personal eller lämna som otilldelat.")
                        }
                    }

                    // Helper Button
                    Button({
                        onClick {
                            val monShifts = patterns.filter { it.dayOfWeek == 1 }
                            val newPatterns = patterns.filter { it.dayOfWeek !in 2..4 }.toMutableList()
                            for (d in 2..4) {
                                monShifts.forEach { s ->
                                    newPatterns.add(s.copy(dayOfWeek = d))
                                }
                            }
                            patterns = newPatterns.sortedBy { it.dayOfWeek }
                        }
                        style {
                            property("padding", "0.45rem 0.85rem")
                            property("border", "1px solid var(--rp-info-border)")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("border-radius", "10px")
                            property("font-weight", "700")
                            property("font-size", "0.78rem")
                            property("cursor", "pointer")
                            property("font-family", "inherit")
                        }
                    }) { Text("Kopiera Måndag → Tis-Tor") }
                }

                // Save Template Box (Inline in Step 2)
                Div({
                    style {
                        property("background", "var(--rp-overlay)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("border-radius", "14px")
                        property("padding", "0.95rem 1.25rem")
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "1rem")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.65rem") } }) {
                        Div({
                            style {
                                property("width", "36px")
                                property("height", "36px")
                                property("border-radius", "10px")
                                property("background", "var(--rp-info-bg)")
                                property("border", "1px solid var(--rp-info-border)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("flex-shrink", "0")
                            }
                        }) {
                            FaIcon("fa-solid fa-bookmark", size = "0.9rem", color = "var(--rp-iris)")
                        }
                        Div {
                            Div({ style { property("font-weight", "800"); property("font-size", "0.88rem"); property("color", "var(--rp-text)") } }) {
                                Text("Spara detta mönster som mall?")
                            }
                            Div({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) {
                                Text("Spara mönstret för återanvändning vid framtida schemaplanering.")
                            }
                        }
                    }

                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                        Input(type = InputType.Text) {
                            placeholder("Mallnamn (t.ex. Sommarschema 2026)")
                            value(templateNameInput)
                            onInput { templateNameInput = it.value }
                            style {
                                property("padding", "0.5rem 0.85rem")
                                property("border", "1.5px solid var(--rp-hl-med)")
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-text)")
                                property("border-radius", "10px")
                                property("font-size", "0.85rem")
                                property("font-family", "inherit")
                                property("min-width", "220px")
                            }
                        }
                        Button({
                            onClick {
                                saveCurrentPatternAsTemplate(templateNameInput)
                            }
                            style {
                                property("padding", "0.5rem 1.15rem")
                                property("border", "none")
                                property("background", "var(--rp-pine)")
                                property("color", "#FFFFFF")
                                property("border-radius", "10px")
                                property("font-weight", "800")
                                property("font-size", "0.82rem")
                                property("cursor", "pointer")
                                property("font-family", "inherit")
                                property("box-shadow", "0 2px 6px rgba(108,60,225,0.2)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.4rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-floppy-disk", size = "0.75rem", color = "var(--rp-surface)")
                            Text("Spara mall")
                        }
                    }
                }

                templateSavedFeedback?.let { fb ->
                    Div({
                        style {
                            property("background", "var(--rp-ok-bg)")
                            property("border", "1px solid var(--rp-ok-border)")
                            property("border-radius", "10px")
                            property("padding", "0.55rem 0.85rem")
                            property("color", "var(--rp-ok-text)")
                            property("font-weight", "700")
                            property("font-size", "0.82rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.45rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-check", size = "0.75rem", color = "var(--rp-ok-text)")
                        Text(fb)
                    }
                }

                // 7 Days Grid
                val dayNames = listOf("Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag")

                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                    dayNames.forEachIndexed { dayIdx, dayTitle ->
                        val dayNum = dayIdx + 1
                        val dayShifts = patterns.filter { it.dayOfWeek == dayNum }

                        Div({
                            style {
                                property("background", "var(--rp-surface)")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "14px")
                                property("padding", "1rem 1.15rem")
                            }
                        }) {
                            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-bottom", "0.75rem") } }) {
                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                                    Span({
                                        style {
                                            property("background", "var(--rp-pine)")
                                            property("color", "#FFFFFF")
                                            property("width", "24px")
                                            property("height", "24px")
                                            property("border-radius", "50%")
                                            property("display", "flex")
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                            property("font-weight", "800")
                                            property("font-size", "0.75rem")
                                        }
                                    }) { Text(dayNum.toString()) }
                                    H4({ style { property("margin", "0"); property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) { Text(dayTitle) }
                                    Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.78rem"); property("font-weight", "600") } }) {
                                        Text("(${dayShifts.size} ${if (dayShifts.size == 1) "pass" else "pass"})")
                                    }
                                }

                                Button({
                                    onClick {
                                        val sT = "08:00"
                                        val eT = "17:00"
                                        val defBreak = calculatePatternBreak(sT, eT)
                                        val newShift = DayPatternItem(dayOfWeek = dayNum, startTime = sT, endTime = eT, breakMinutes = defBreak, assignedToUserId = null)
                                        patterns = (patterns + newShift).sortedBy { it.dayOfWeek }
                                    }
                                    style {
                                        property("padding", "0.35rem 0.75rem")
                                        property("border", "1px solid var(--rp-pine)")
                                        property("background", "var(--rp-surface)")
                                        property("color", "var(--rp-pine)")
                                        property("border-radius", "8px")
                                        property("font-weight", "700")
                                        property("font-size", "0.78rem")
                                        property("cursor", "pointer")
                                        property("font-family", "inherit")
                                    }
                                }) { Text("+ Lägg till pass") }
                            }

                            if (dayShifts.isEmpty()) {
                                Div({ style { property("color", "var(--rp-muted)"); property("font-size", "0.8rem"); property("font-style", "italic"); property("padding", "0.3rem 0") } }) {
                                    Text("Inga pass konfigurerade för denna dag (Ledig dag).")
                                }
                            } else {
                                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.6rem") } }) {
                                    dayShifts.forEachIndexed { sIdx, shift ->
                                        Div({
                                            style {
                                                property("background", "var(--rp-surface)")
                                                property("border", "1px solid var(--rp-hl-med)")
                                                property("border-radius", "10px")
                                                property("padding", "0.6rem 0.85rem")
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("gap", "0.75rem")
                                                property("flex-wrap", "wrap")
                                            }
                                        }) {
                                            // Start Time
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                                Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("Start:") }
                                                Input(type = InputType.Text) {
                                                    value(shift.startTime)
                                                    onInput { e ->
                                                        val updated = patterns.toMutableList()
                                                        val targetIdx = patterns.indexOf(shift)
                                                        if (targetIdx >= 0) {
                                                            val newStart = e.value
                                                            val autoB = calculatePatternBreak(newStart, shift.endTime)
                                                            updated[targetIdx] = shift.copy(startTime = newStart, breakMinutes = autoB)
                                                            patterns = updated
                                                        }
                                                    }
                                                    style {
                                                        property("width", "65px")
                                                        property("padding", "0.3rem 0.45rem")
                                                        property("border", "1px solid var(--rp-hl-med)")
                                                        property("border-radius", "6px")
                                                        property("font-size", "0.85rem")
                                                        property("font-weight", "700")
                                                        property("text-align", "center")
                                                    }
                                                }
                                            }

                                            // End Time
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                                Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("Slut:") }
                                                Input(type = InputType.Text) {
                                                    value(shift.endTime)
                                                    onInput { e ->
                                                        val updated = patterns.toMutableList()
                                                        val targetIdx = patterns.indexOf(shift)
                                                        if (targetIdx >= 0) {
                                                            val newEnd = e.value
                                                            val autoB = calculatePatternBreak(shift.startTime, newEnd)
                                                            updated[targetIdx] = shift.copy(endTime = newEnd, breakMinutes = autoB)
                                                            patterns = updated
                                                        }
                                                    }
                                                    style {
                                                        property("width", "65px")
                                                        property("padding", "0.3rem 0.45rem")
                                                        property("border", "1px solid var(--rp-hl-med)")
                                                        property("border-radius", "6px")
                                                        property("font-size", "0.85rem")
                                                        property("font-weight", "700")
                                                        property("text-align", "center")
                                                    }
                                                }
                                            }

                                            // Break Duration Pill / Selector
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                                Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("Rast:") }
                                                Select({
                                                    onChange { e ->
                                                        val bVal = e.value?.toIntOrNull() ?: 0
                                                        val updated = patterns.toMutableList()
                                                        val targetIdx = patterns.indexOf(shift)
                                                        if (targetIdx >= 0) {
                                                            updated[targetIdx] = shift.copy(breakMinutes = bVal)
                                                            patterns = updated
                                                        }
                                                    }
                                                    style {
                                                        property("padding", "0.3rem 0.45rem")
                                                        property("border", "1px solid var(--rp-hl-med)")
                                                        property("border-radius", "6px")
                                                        property("font-size", "0.82rem")
                                                        property("font-weight", "700")
                                                        property("color", "var(--rp-text)")
                                                    }
                                                }) {
                                                    listOf(0 to "0 min", 30 to "30 min", 45 to "45 min", 60 to "1h").forEach { (bm, lbl) ->
                                                        Option(bm.toString(), attrs = { if (shift.breakMinutes == bm) selected() }) {
                                                            Text(lbl)
                                                        }
                                                    }
                                                }
                                            }

                                            // Assigned Staff Dropdown
                                            Div({ style { property("flex", "1"); property("min-width", "180px") } }) {
                                                Select({
                                                    onChange { e ->
                                                        val selVal = e.value
                                                        val newAssignedId = if (selVal == null || selVal == "unassigned") null else selVal.toLongOrNull()
                                                        val updated = patterns.toMutableList()
                                                        val targetIdx = patterns.indexOf(shift)
                                                        if (targetIdx >= 0) {
                                                            updated[targetIdx] = shift.copy(assignedToUserId = newAssignedId)
                                                            patterns = updated
                                                        }
                                                    }
                                                    style {
                                                        property("width", "100%")
                                                        property("padding", "0.35rem 0.55rem")
                                                        property("border", "1px solid var(--rp-hl-med)")
                                                        property("border-radius", "6px")
                                                        property("font-size", "0.82rem")
                                                        property("font-weight", "600")
                                                        property("font-family", "inherit")
                                                    }
                                                }) {
                                                    Option("unassigned", attrs = { if (shift.assignedToUserId == null) selected() }) {
                                                        Text("Ingen vald (Otilldelat pass / Öppet för ansökan)")
                                                    }
                                                    staff.forEach { member ->
                                                        val isSel = shift.assignedToUserId == member.userId
                                                        Option(member.userId.toString(), attrs = { if (isSel) selected() }) {
                                                            Text("Tilldela: ${member.email}")
                                                        }
                                                    }
                                                }
                                            }

                                            // Remove Shift Button
                                            Button({
                                                onClick {
                                                    val updated = patterns.toMutableList()
                                                    updated.remove(shift)
                                                    patterns = updated
                                                }
                                                style {
                                                    property("padding", "0.3rem 0.55rem")
                                                    property("border", "none")
                                                    property("background", "var(--rp-err-bg)")
                                                    property("color", "var(--rp-err-text)")
                                                    property("border-radius", "6px")
                                                    property("font-weight", "800")
                                                    property("font-size", "0.78rem")
                                                    property("cursor", "pointer")
                                                }
                                            }) { Text("Ta bort") }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Action Bar
                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-top", "1rem") } }) {
                    Button({
                        onClick { step = 1 }
                        style {
                            property("padding", "0.6rem 1.4rem")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-subtle)")
                            property("border-radius", "50px")
                            property("font-weight", "700")
                            property("font-size", "0.85rem")
                            property("cursor", "pointer")
                            property("font-family", "inherit")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.45rem")
                            property("transition", "all 0.15s ease")
                        }
                    }) {
                        FaIcon("fa-solid fa-arrow-left", size = "0.75rem", color = "var(--rp-subtle)")
                        Text("Tillbaka")
                    }

                    Button({
                        onClick { step = 3 }
                        style {
                            property("padding", "0.65rem 1.6rem")
                            property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "#FFFFFF")
                            property("border-radius", "50px")
                            property("font-weight", "700")
                            property("font-size", "0.88rem")
                            property("cursor", "pointer")
                            property("font-family", "inherit")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                            property("box-shadow", "0 2px 10px rgba(108,60,225,0.25)")
                            property("transition", "all 0.15s ease")
                        }
                    }) {
                        Text("Förhandsgranska & Rulla ut")
                        FaIcon("fa-solid fa-arrow-right", size = "0.8rem", color = "var(--rp-surface)")
                    }
                }
            }
        }

        // ── STEP 3: Förhandsgranska & Massgenerera ──
        if (step == 3) {
            val totalShiftsPerWeek = patterns.size
            val totalShiftsGenerated = totalShiftsPerWeek * numberOfWeeks
            val unassignedCountPerWeek = patterns.count { it.assignedToUserId == null }
            val assignedCountPerWeek = patterns.count { it.assignedToUserId != null }

            val endDateStr = addDays(startDate, numberOfWeeks * 7 - 1)

            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "20px")
                    property("padding", "1.5rem 1.75rem")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "1.5rem")
                }
            }) {
                Div {
                    H3({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("font-size", "1.15rem"); property("color", "var(--rp-text)") } }) {
                        Text("Steg 3: Förhandsgranska & Generera Schema")
                    }
                    P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                        Text("Granska sammanställningen och klicka på 'Skapa och publicera schema' för att rulla ut passen till databasen.")
                    }
                }

                // Summary Cards Grid
                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    SummaryMetricCard("fa-calendar-week", "TIDSPERIOD", "$startDate – $endDateStr", "$numberOfWeeks veckors rullning", "var(--rp-info-bg)", "var(--rp-pine)")
                    SummaryMetricCard("fa-cubes", "TOTALT PASS", "$totalShiftsGenerated pass", "$totalShiftsPerWeek pass/vecka", "var(--rp-info-bg)", "var(--rp-pine)")
                    SummaryMetricCard("fa-user-check", "FÖRVALDA PASS", "${assignedCountPerWeek * numberOfWeeks} pass", "Direkt tilldelade", "var(--rp-ok-bg)", "var(--rp-ok-text)")
                    SummaryMetricCard("fa-door-open", "ÖPPNA PASS", "${unassignedCountPerWeek * numberOfWeeks} pass", "Kan sökas av personal", "var(--rp-warn-bg)", "var(--rp-warn-text)")
                }

                // Breakdown list
                Div({
                    style {
                        property("background", "var(--rp-overlay)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("border-radius", "16px")
                        property("padding", "1.2rem 1.35rem")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("margin-bottom", "0.85rem") } }) {
                        FaIcon("fa-solid fa-list-ul", size = "0.95rem", color = "var(--rp-pine)")
                        H4({ style { property("margin", "0"); property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                            Text("Mönstersammanställning (Veckomall)")
                        }
                    }

                    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.6rem") } }) {
                        val dayNames = listOf("Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag")
                        dayNames.forEachIndexed { idx, dName ->
                            val dNum = idx + 1
                            val dShifts = patterns.filter { it.dayOfWeek == dNum }
                            if (dShifts.isNotEmpty()) {
                                Div({
                                    style {
                                        property("display", "flex")
                                        property("justify-content", "space-between")
                                        property("align-items", "center")
                                        property("font-size", "0.85rem")
                                        property("background", "var(--rp-surface)")
                                        property("border", "1px solid var(--rp-hl-med)")
                                        property("border-radius", "10px")
                                        property("padding", "0.6rem 0.85rem")
                                    }
                                }) {
                                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                        Span({
                                            style {
                                                property("background", "var(--rp-info-bg)")
                                                property("color", "var(--rp-pine)")
                                                property("padding", "0.15rem 0.5rem")
                                                property("border-radius", "6px")
                                                property("font-weight", "800")
                                                property("font-size", "0.74rem")
                                            }
                                        }) { Text(dName) }
                                    }
                                    Span({ style { property("color", "var(--rp-text)"); property("font-weight", "600"); property("font-size", "0.82rem") } }) {
                                        val text = dShifts.joinToString(" • ") { s ->
                                            val assignee = if (s.assignedToUserId == null) "Otilldelat" else staff.find { it.userId == s.assignedToUserId }?.email ?: "Tilldelad"
                                            val breakTxt = if (s.breakMinutes > 0) ", ${s.breakMinutes}m rast" else ""
                                            "${s.startTime.take(5)}–${s.endTime.take(5)}$breakTxt ($assignee)"
                                        }
                                        Text(text)
                                    }
                                }
                            }
                        }
                    }
                }

                // Overwrite Option Checkbox (only shown before generation)
                if (!isGenerated) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem") } }) {
                        Input(type = InputType.Checkbox) {
                            id("overwriteCheck")
                            checked(overwriteExisting)
                            onChange { overwriteExisting = it.value }
                        }
                        Label(forId = "overwriteCheck", attrs = { style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("cursor", "pointer") } }) {
                            Text("Ersätt befintliga pass om krockar finns under denna period")
                        }
                    }
                }

                // ── SUCCESS VISUALIZER OR GENERATE BUTTON ──
                if (isGenerated) {
                    // Modern Clean Success Card (matching light design system)
                    Div({
                        style {
                            property("background", "var(--rp-surface)")
                            property("border", "1.5px solid var(--rp-ok-border)")
                            property("border-radius", "20px")
                            property("padding", "1.75rem 2rem")
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "1.35rem")
                            property("box-shadow", "0 10px 30px rgba(16,185,129,0.08)")
                        }
                    }) {
                        // Top Header: Checkmark badge + Title & Subtitle
                        Div({
                            style {
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "1.25rem")
                                property("flex-wrap", "wrap")
                            }
                        }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1rem") } }) {
                                Div({
                                    style {
                                        property("width", "52px")
                                        property("height", "52px")
                                        property("border-radius", "16px")
                                        property("background", "var(--rp-ok-bg)")
                                        property("border", "1px solid var(--rp-ok-border)")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("flex-shrink", "0")
                                    }
                                }) {
                                    FaIcon("fa-solid fa-circle-check", size = "1.75rem", color = "var(--rp-ok-text)")
                                }

                                Div {
                                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("margin-bottom", "0.2rem") } }) {
                                        H3({ style { property("margin", "0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                                            Text("Schemat har skapats & rullats ut!")
                                        }
                                        Span({
                                            style {
                                                property("background", "var(--rp-ok-bg)")
                                                property("color", "var(--rp-ok-text)")
                                                property("border", "1px solid var(--rp-ok-border)")
                                                property("padding", "0.2rem 0.65rem")
                                                property("border-radius", "50px")
                                                property("font-size", "0.72rem")
                                                property("font-weight", "800")
                                            }
                                        }) { Text("KLART") }
                                    }
                                    P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.9rem"); property("font-weight", "500") } }) {
                                        Text("${generatedCount ?: totalShiftsGenerated} pass har skapats för $numberOfWeeks veckor ($startDate till $endDateStr).")
                                    }
                                }
                            }

                            // Quick stats pill
                            Div({
                                style {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.75rem")
                                    property("background", "var(--rp-overlay)")
                                    property("border", "1px solid var(--rp-hl-med)")
                                    property("border-radius", "12px")
                                    property("padding", "0.6rem 1rem")
                                }
                            }) {
                                Div {
                                    Span({ style { property("font-size", "0.68rem"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("display", "block") } }) { Text("PERIOD") }
                                    Span({ style { property("font-size", "0.85rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("$numberOfWeeks v ($startDate)") }
                                }
                                Div({ style { property("width", "1px"); property("height", "24px"); property("background", "var(--rp-hl-med)") } })
                                Div {
                                    Span({ style { property("font-size", "0.68rem"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("display", "block") } }) { Text("PASS") }
                                    Span({ style { property("font-size", "0.85rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)") } }) { Text("${generatedCount ?: totalShiftsGenerated} st") }
                                }
                            }
                        }

                        // Save as Template sub-panel
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "14px")
                                property("padding", "0.95rem 1.25rem")
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "1rem")
                                property("flex-wrap", "wrap")
                            }
                        }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.65rem") } }) {
                                Div({
                                    style {
                                        property("width", "36px")
                                        property("height", "36px")
                                        property("border-radius", "10px")
                                        property("background", "var(--rp-info-bg)")
                                        property("border", "1px solid var(--rp-info-border)")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("flex-shrink", "0")
                                    }
                                }) {
                                    FaIcon("fa-solid fa-bookmark", size = "0.9rem", color = "var(--rp-iris)")
                                }
                                Div {
                                    Div({ style { property("font-weight", "800"); property("font-size", "0.88rem"); property("color", "var(--rp-text)") } }) {
                                        Text("Spara detta veckomönster som mall?")
                                    }
                                    Div({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) {
                                        Text("Återanvänd enkelt mönstret vid framtida perioder.")
                                    }
                                }
                            }

                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                Input(type = InputType.Text) {
                                    placeholder("Mallnamn (t.ex. Sommarschema 2026)")
                                    value(templateNameInput)
                                    onInput { templateNameInput = it.value }
                                    style {
                                        property("padding", "0.5rem 0.85rem")
                                        property("border", "1.5px solid var(--rp-hl-med)")
                                        property("background", "var(--rp-surface)")
                                        property("color", "var(--rp-text)")
                                        property("border-radius", "10px")
                                        property("font-size", "0.85rem")
                                        property("font-family", "inherit")
                                        property("min-width", "220px")
                                    }
                                }
                                Button({
                                    onClick {
                                        saveCurrentPatternAsTemplate(templateNameInput)
                                    }
                                    style {
                                        property("padding", "0.5rem 1.15rem")
                                        property("border", "none")
                                        property("background", "var(--rp-pine)")
                                        property("color", "#FFFFFF")
                                        property("border-radius", "10px")
                                        property("font-weight", "800")
                                        property("font-size", "0.82rem")
                                        property("cursor", "pointer")
                                        property("font-family", "inherit")
                                        property("box-shadow", "0 2px 6px rgba(108,60,225,0.2)")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("gap", "0.4rem")
                                    }
                                }) {
                                    FaIcon("fa-solid fa-floppy-disk", size = "0.75rem", color = "var(--rp-surface)")
                                    Text("Spara mall")
                                }
                            }
                        }

                        templateSavedFeedback?.let { fb ->
                            Div({
                                style {
                                    property("background", "var(--rp-ok-bg)")
                                    property("border", "1px solid var(--rp-ok-border)")
                                    property("border-radius", "10px")
                                    property("padding", "0.55rem 0.85rem")
                                    property("color", "var(--rp-ok-text)")
                                    property("font-weight", "700")
                                    property("font-size", "0.82rem")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.45rem")
                                }
                            }) {
                                FaIcon("fa-solid fa-check", size = "0.75rem", color = "var(--rp-ok-text)")
                                Text(fb)
                            }
                        }

                        // Navigation CTAs inside Success Card
                        Div({
                            style {
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "0.85rem")
                                property("flex-wrap", "wrap")
                                property("padding-top", "0.5rem")
                                property("border-top", "1px solid var(--rp-hl-med)")
                            }
                        }) {
                            Button({
                                onClick {
                                    step = 1
                                    isGenerated = false
                                    generatedCount = null
                                    templateSavedFeedback = null
                                }
                                style {
                                    property("padding", "0.65rem 1.3rem")
                                    property("border", "1px solid var(--rp-hl-med)")
                                    property("background", "var(--rp-surface)")
                                    property("color", "var(--rp-subtle)")
                                    property("border-radius", "50px")
                                    property("font-weight", "700")
                                    property("font-size", "0.85rem")
                                    property("cursor", "pointer")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.5rem")
                                    property("font-family", "inherit")
                                    property("transition", "all 0.15s ease")
                                }
                            }) {
                                FaIcon("fa-solid fa-rotate-left", size = "0.8rem", color = "var(--rp-subtle)")
                                Text("Skapa ytterligare ett schema")
                            }

                            if (onNavigateToSchema != null) {
                                Button({
                                    onClick { onNavigateToSchema() }
                                    style {
                                        property("padding", "0.65rem 1.6rem")
                                        property("border", "none")
                                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                                        property("color", "#FFFFFF")
                                        property("border-radius", "50px")
                                        property("font-weight", "800")
                                        property("font-size", "0.88rem")
                                        property("cursor", "pointer")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("gap", "0.55rem")
                                        property("font-family", "inherit")
                                        property("box-shadow", "0 3px 12px rgba(108,60,225,0.28)")
                                        property("transition", "all 0.15s ease")
                                    }
                                }) {
                                    FaIcon("fa-solid fa-calendar-days", size = "0.85rem", color = "var(--rp-surface)")
                                    Text("Gå till schemat och granska pass")
                                }
                            }
                        }
                    }
                } else {
                    // Standard Generate Button Bar (before creation)
                    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-top", "1rem") } }) {
                        Button({
                            onClick { step = 2 }
                            style {
                                property("padding", "0.6rem 1.4rem")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-subtle)")
                                property("border-radius", "50px")
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                                property("font-family", "inherit")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.45rem")
                                property("transition", "all 0.15s ease")
                            }
                        }) {
                            FaIcon("fa-solid fa-arrow-left", size = "0.75rem", color = "var(--rp-subtle)")
                            Text("Ändra mönster")
                        }

                        Button({
                            if (isSubmitting) disabled()
                            onClick {
                                sbScope.launch {
                                    isSubmitting = true
                                    errorMsg = null
                                    val req = GenerateBulkScheduleRequest(
                                        startDate = startDate,
                                        numberOfWeeks = numberOfWeeks,
                                        patterns = patterns,
                                        overwriteExisting = overwriteExisting
                                    )
                                    slotsRepo.generateBulkSchedule(workplaceId, req).onSuccess { count ->
                                        isGenerated = true
                                        generatedCount = count
                                        isSubmitting = false
                                    }.onFailure { err ->
                                        errorMsg = "Kunde inte generera schema: ${err.message}"
                                        isSubmitting = false
                                    }
                                }
                            }
                            style {
                                property("padding", "0.75rem 1.75rem")
                                property("border", "none")
                                property("background", if (isSubmitting) "var(--rp-muted)" else "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))")
                                property("color", "#FFFFFF")
                                property("border-radius", "50px")
                                property("font-weight", "700")
                                property("font-size", "0.95rem")
                                property("cursor", if (isSubmitting) "not-allowed" else "pointer")
                                property("font-family", "inherit")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.55rem")
                                property("box-shadow", "0 4px 14px rgba(5,150,105,0.3)")
                                property("transition", "all 0.15s ease")
                            }
                        }) {
                            FaIcon("fa-solid fa-wand-magic-sparkles", size = "0.9rem", color = "var(--rp-surface)")
                            val label = if (isSubmitting) "Skapar schema..." else "Skapa och publicera schema ($totalShiftsGenerated pass)"
                            Text(label)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StepBadge(num: Int, title: String, isActive: Boolean, isDone: Boolean, onClick: () -> Unit) {
    Div({
        onClick { onClick() }
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.45rem")
            property("cursor", "pointer")
            property("padding", "0.3rem 0.75rem")
            property("border-radius", "50px")
            property("background", if (isActive) "var(--rp-surface)" else "transparent")
            property("color", if (isActive) "var(--rp-text)" else "var(--rp-subtle)")
            property("font-weight", if (isActive) "800" else "600")
            property("font-size", "0.8rem")
            property("box-shadow", if (isActive) "0 2px 6px rgba(0,0,0,0.06)" else "none")
            property("transition", "all 0.15s ease")
        }
    }) {
        Span({
            style {
                property("width", "20px")
                property("height", "20px")
                property("border-radius", "50%")
                property("background", if (isActive) "var(--rp-pine)" else if (isDone) "var(--rp-pine)" else "var(--rp-overlay)")
                property("color", if (isActive || isDone) "#FFFFFF" else "var(--rp-subtle)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("font-size", "0.7rem")
                property("font-weight", "800")
            }
        }) {
            if (isDone) {
                FaIcon("fa-solid fa-check", size = "0.65rem", color = "var(--rp-surface)")
            } else {
                Text(num.toString())
            }
        }
        Text(title)
    }
}

@Composable
private fun SummaryMetricCard(icon: String, title: String, value: String, subtext: String, bgColor: String, textColor: String) {
    Div({
        style {
            property("background", "var(--rp-surface)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "16px")
            property("padding", "1rem 1.15rem")
            property("box-shadow", "0 2px 6px rgba(0,0,0,0.02)")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.85rem")
        }
    }) {
        Div({
            style {
                property("width", "40px")
                property("height", "40px")
                property("border-radius", "12px")
                property("background", bgColor)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
        }) {
            FaIcon("fa-solid $icon", size = "1rem", color = textColor)
        }
        Div {
            Span({ style { property("font-size", "0.68rem"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("letter-spacing", "0.5px"); property("display", "block") } }) { Text(title) }
            Div({ style { property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("margin", "0.05rem 0") } }) { Text(value) }
            Span({ style { property("font-size", "0.74rem"); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) { Text(subtext) }
        }
    }
}

// ── Default Pattern Templates ──
private fun createDefaultNormalPatterns(): List<DayPatternItem> {
    val list = mutableListOf<DayPatternItem>()
    for (d in 1..4) {
        list.add(DayPatternItem(dayOfWeek = d, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
    }
    list.add(DayPatternItem(dayOfWeek = 5, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
    list.add(DayPatternItem(dayOfWeek = 5, startTime = "12:00", endTime = "17:00", breakMinutes = 30))
    list.add(DayPatternItem(dayOfWeek = 6, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
    list.add(DayPatternItem(dayOfWeek = 6, startTime = "12:00", endTime = "17:00", breakMinutes = 30))
    list.add(DayPatternItem(dayOfWeek = 7, startTime = "10:00", endTime = "16:00", breakMinutes = 30))
    return list
}

private fun createSummerPatterns(): List<DayPatternItem> {
    val list = mutableListOf<DayPatternItem>()
    for (d in 1..7) {
        list.add(DayPatternItem(dayOfWeek = d, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
        list.add(DayPatternItem(dayOfWeek = d, startTime = "12:00", endTime = "20:00", breakMinutes = 60))
    }
    return list
}

private fun createWeekendPeakPatterns(): List<DayPatternItem> {
    val list = mutableListOf<DayPatternItem>()
    for (d in 1..4) {
        list.add(DayPatternItem(dayOfWeek = d, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
    }
    for (d in 5..7) {
        list.add(DayPatternItem(dayOfWeek = d, startTime = "08:00", endTime = "17:00", breakMinutes = 60))
        list.add(DayPatternItem(dayOfWeek = d, startTime = "12:00", endTime = "21:00", breakMinutes = 60))
    }
    return list
}
